(() => {
  if (window.__himpCollectionScroll) return;
  window.__himpCollectionScroll = true;
  const selector = '[data-infinite-collection]';
  const initialise = root => {
    if (root.infiniteCollection) return;
    let dynamic, observer, controller, loading = false, generation = 0, suspended = false, restoring = false;
    const loaded = new Set();
    const previous = window.history?.state?.himpCollectionScroll;
    const returning = window.performance?.getEntriesByType?.('navigation')[0]?.type === 'back_forward';
    const footer = () => root.querySelector('[data-collection-scroll]');
    const link = () => footer()?.querySelector('[data-collection-more]');
    const setStatus = (message, error = false) => {
      const node = footer(); if (!node) return;
      node.classList.toggle('is-error', error);
      node.querySelector('[data-collection-scroll-status]').textContent = message;
    };
    const urlFor = value => {
      const url = new URL(value, location.href);
      if (url.origin !== location.origin || url.pathname !== location.pathname) throw new Error('Invalid collection page');
      return url;
    };
    const stop = () => {
      generation++; controller?.abort(); controller = null;
      observer?.disconnect(); observer = null; loading = false;
      const node = footer();
      node?.classList.remove('is-loading');
      link()?.removeAttribute('aria-disabled');
      root.querySelector('.collection-redesign__grid')?.removeAttribute('aria-busy');
    };
    const observe = () => {
      observer?.disconnect();
      if (!link() || !('IntersectionObserver' in window) || suspended || restoring) return;
      observer = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) append();
      }, { rootMargin: '500px 0px', threshold: 0 });
      observer.observe(footer());
    };
    const append = async () => {
      const nextLink = link(), grid = root.querySelector('.collection-redesign__grid');
      if (loading || suspended || !nextLink || !grid || root.classList.contains('is-loading')) return;
      let url;
      try { url = urlFor(nextLink.href); } catch (error) { setStatus(footer().dataset.errorText, true); return; }
      if (loaded.has(url.href)) return;
      const epoch = generation, activeDynamic = dynamic, node = footer();
      loading = true; observer?.disconnect();
      const request = new AbortController(); controller = request;
      const timeout = setTimeout(() => request.abort(), 15000);
      node.classList.add('is-loading'); node.classList.remove('is-error');
      grid.setAttribute('aria-busy', 'true'); nextLink.setAttribute('aria-disabled', 'true');
      setStatus(node.dataset.loadingText);
      try {
        const response = await fetch(url.href, { credentials: 'same-origin', headers: { 'X-Requested-With': 'XMLHttpRequest' }, signal: request.signal });
        if (!response.ok) throw new Error('Collection page failed');
        const page = new DOMParser().parseFromString(await response.text(), 'text/html');
        const rankedRoot = page.querySelector(selector);
        if (rankedRoot?.hasAttribute('data-default-sort') && window.himpStockSort) await window.himpStockSort.prepare(rankedRoot, url.href, request.signal);
        if (epoch !== generation || !root.isConnected || root.classList.contains('is-loading') || activeDynamic !== root.querySelector('[data-collection-dynamic]')) return;
        const nextRoot = page.querySelector(selector);
        const incoming = nextRoot?.querySelector('.collection-redesign__grid');
        const nextFooter = nextRoot?.querySelector('[data-collection-scroll]');
        if (!incoming || !nextFooter) throw new Error('Collection content unavailable');
        const ids = new Set([...grid.querySelectorAll('[data-product-id]')].map(card => card.dataset.productId));
        const cards = [...incoming.querySelectorAll('[data-collection-card]')].filter(card => !ids.has(card.dataset.productId));
        const following = nextFooter.querySelector('[data-collection-more]');
        if (!cards.length && following) throw new Error('Collection page repeated');
        const nextURL = following ? urlFor(following.href) : null;
        if (nextURL && (nextURL.href === url.href || loaded.has(nextURL.href))) throw new Error('Collection page repeated');
        const fragment = document.createDocumentFragment();
        cards.forEach(card => {
          const copy = document.importNode(card, true); copy.classList.add('is-visible'); fragment.append(copy);
        });
        grid.append(fragment); loaded.add(url.href);
        if (following) {
          nextLink.href = nextURL.href;
          nextLink.textContent = following.textContent;
          setStatus(node.dataset.loadedText);
        } else {
          nextLink.remove(); setStatus(node.dataset.endText);
        }
      } catch (error) {
        if (epoch !== generation || !root.isConnected) return;
        setStatus(node.dataset.errorText, true);
        nextLink.textContent = node.dataset.retryText;
      } finally {
        clearTimeout(timeout);
        if (epoch === generation) {
          loading = false; controller = null;
          node.classList.remove('is-loading'); grid.removeAttribute('aria-busy');
          nextLink.removeAttribute('aria-disabled');
          if (!node.classList.contains('is-error')) observe();
        }
      }
    };
    const reconcile = () => {
      if (root.classList.contains('is-loading')) {
        if (!suspended) { suspended = true; stop(); }
        return;
      }
      const next = root.querySelector('[data-collection-dynamic]');
      if (next !== dynamic || suspended) {
        stop(); dynamic = next; suspended = false; loaded.clear();
        observe();
      }
    };
    const click = event => {
      const target = event.target.closest('[data-collection-more]');
      if (!target || !root.contains(target) || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault(); append();
    };
    root.addEventListener('click', click);
    const remember = () => {
      if (window.Shopify?.designMode) return;
      window.history.replaceState({ ...window.history.state, himpCollectionScroll: { url: location.href, pages: [...loaded], scrollY: window.scrollY } }, '', location.href);
    };
    window.addEventListener('pagehide', remember);
    const changes = new MutationObserver(reconcile);
    changes.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
    root.infiniteCollection = { destroy() { suspended = true; stop(); changes.disconnect(); root.removeEventListener('click', click); window.removeEventListener('pagehide', remember); delete root.infiniteCollection; } };
    if (returning && previous?.url === location.href && !window.Shopify?.designMode) restoring = true;
    reconcile();
    if (restoring) (async () => {
      try {
        for (const oldPage of previous.pages) {
          if (!root.isConnected || !link() || urlFor(link().href).href !== oldPage) break;
          const before = loaded.size; await append();
          if (loaded.size === before) break;
        }
        if (loaded.size === previous.pages.length) window.scrollTo({ top: previous.scrollY, behavior: 'instant' });
      } finally { restoring = false; observe(); }
    })();
  };
  const initialiseAll = (scope = document) => scope.querySelectorAll(selector).forEach(initialise);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialiseAll());
  else initialiseAll();
  document.addEventListener('shopify:section:load', event => initialiseAll(event.target));
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll(selector).forEach(root => root.infiniteCollection?.destroy()));
})();
